import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'

import { useSearchParams } from 'react-router-dom'
import { Button } from '../../components/button'
import { DateRangePicker } from '../../components/inputs/dateRange'
import Dropdown from '../../components/inputs/dropdown'
import { SearchInput } from '../../components/inputs/searchInput'
import { FilterAuditLogsModal } from '../../components/modal/app_modals/filterAuditLogsModal'
import { TableComponent } from '../../components/table'
import { getPageFromSkip, getSkipFromPage, parseDate } from '../../helpers'
import { useModalControl } from '../../hooks/useModalControl'
import { useGetAllAuditLogsQuery } from '../../redux-toolkits/audit-logs/auditLogs.slice'
import { QueryPropsLogs } from '../../redux-toolkits/audit-logs/auditLogs.types'

const AllLogs: React.FC = () => {
  const [query, setQuery] = useState<QueryPropsLogs>()

  const [searchParams, setSearchParams] = useSearchParams()

  const { modal, modalOpen, setModalOpen, handleModalClose, handleModalOpen } =
    useModalControl()

  const { data, isFetching } = useGetAllAuditLogsQuery({
    ...query,
  })

  useEffect(() => {
    setQuery({
      skip: Number(searchParams.get('skip') || 0),
      limit: 20,
      search: searchParams.get('search') || undefined,
      module: searchParams.get('module') || undefined,
      adminId: searchParams.get('adminId') || undefined,
      adminRole: searchParams.get('adminRole') || undefined,
      startDate: searchParams.get('startDate') || undefined,
      endDate: searchParams.get('endDate') || undefined,
    })
  }, [searchParams])

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-center justify-end">
        <Dropdown
          menuButton={
            <Button
              label={`${
                searchParams.get('startDate')
                  ? format(searchParams.get('startDate')!, 'dd MMM, yyyy')
                  : 'Start date'
              } - ${
                searchParams.get('endDate')
                  ? format(searchParams.get('endDate')!, 'dd MMM, yyyy')
                  : 'End date'
              }`}
              primary={false}
              className="!font-normal !w-full md:max-w-fit !py-0 !px-0"
              variant="plain"
              icon="calendar"
            />
          }
          menuClassName="!min-w-max !border-[0px]"
          customMenuItems={
            <DateRangePicker
              required
              navLayout="around"
              animate
              mode="range"
              selected={{
                from:
                  parseDate(searchParams.get('startDate'))?.toDate() ??
                  undefined,
                to:
                  parseDate(searchParams.get('endDate'))?.toDate() ?? undefined,
              }}
              onSelect={(selected) => {
                if (selected?.from && selected?.to) {
                  setSearchParams({
                    ...Object.fromEntries(searchParams),
                    startDate: format(selected?.from, 'yyyy-MM-dd'),
                    endDate: format(selected?.to, 'yyyy-MM-dd'),
                  })
                }
              }}
              numberOfMonths={2}
              disabled={{ after: new Date() }}
            />
          }
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="!w-full md:!max-w-[400px]">
          <SearchInput
            placeholder="Search"
            searchFunction={(search) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                search: search,
                skip: '0',
              })
            }}
            className="!w-full md:!max-w-[400px]"
            initialValue={searchParams.get('search') || undefined}
          />
        </div>

        <Button
          label={`Filters`}
          primary={false}
          icon="filter"
          className="!font-normal"
          onClick={() => {
            handleModalOpen('filterModal')
          }}
        />
      </div>

      <div className="w-full overflow-x-scroll">
        <TableComponent
          headers={[
            'User',
            'Role',
            'Activity',
            'User Email',
            'Module',
            'Time of Activity',
          ]}
          rows={
            data?.data?.result?.map((log) => {
              return {
                id: log._id,
                content: [
                  log.adminName,
                  log.adminRole,
                  log.description,
                  log.adminEmail,
                  log.module,
                  `${format(log.createdAt, 'dd/MM/yyyy h:mma')}`,
                ],
              }
            }) ?? []
          }
          name="logs"
          loading={isFetching}
          isEmpty={!data?.data || data.data.count < 1}
          totalDataCount={data?.data.count}
          currentPage={getPageFromSkip(
            Number(query?.skip),
            Number(query?.limit),
          )}
          pageLimit={NUMBER_OF_ITEMS_PER_PAGE}
          onPageChange={(page) => {
            setSearchParams({
              ...Object.fromEntries(searchParams),
              skip: `${getSkipFromPage(page, Number(query?.limit))}`,
            })
          }}
        />
      </div>

      <FilterAuditLogsModal
        modalOpen={modalOpen && modal === 'filterModal'}
        handleModalClose={handleModalClose}
        setModalOpen={setModalOpen}
        query={query}
        onSucess={(q) => {
          setSearchParams({
            ...Object.fromEntries(searchParams),
            module: q.module ?? '',
            adminId: q.adminId ?? '',
            adminRole: q.adminRole ?? '',
            skip: String(q.skip ?? 0),
          })
        }}
      />
    </div>
  )
}

export default AllLogs
